import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Filter, Repeat, CreditCard, Share2, Lightbulb, Users, Settings,
  FileEdit, LogOut, Loader2, TrendingUp, Download, AlertTriangle, CheckCircle2, Menu, X,
} from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import { APP_VERSION } from "@/version";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import FounderDashboard from "@/pages/admin/FounderDashboard";
import FounderCustomers, { FounderDataQuality } from "@/pages/admin/FounderCustomers";
import UsersTab from "@/pages/admin/UsersTab";
import { WeeklyDigest } from "@/pages/admin/WeeklyDigest";
import { GoLiveChecklist, PaymentsSwitch } from "@/pages/admin/GoLive";
import { TrustCms } from "@/pages/admin/TrustCms";

function Section({ title, children }) {
  return <div className="rounded-3xl border border-border bg-card p-6"><h3 className="mb-4 font-display text-lg font-bold">{title}</h3>{children}</div>;
}

function ManualUnlock({ onDone }) {
  const [email, setEmail] = useState("");
  const [plan, setPlan] = useState("monthly");
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState("");
  const [sync, setSync] = useState(null);
  const [st, setSt] = useState(null);
  const loadStatus = () => api.get("/admin/razorpay/sync-status").then(({ data }) => setSt(data)).catch(() => {});
  useEffect(() => { loadStatus(); }, []);
  const activate = async () => {
    setBusy("act");
    try { const { data } = await api.post("/admin/activate-by-email", { email, plan, payment_ref: ref || `manual_${Date.now()}` }); toast.success(`NxT Plus (${data.plan}) activated for ${data.email}`); setEmail(""); setRef(""); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const reconcile = async () => {
    setBusy("sync"); setSync(null);
    try { const { data } = await api.post("/admin/razorpay/reconcile"); setSync(data); toast.success(`Synced · ${data.activated.length} activated · ${data.unmatched.length} unmatched`); onDone(); loadStatus(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const last = st?.last;
  return (
    <div className="mb-4 rounded-2xl border border-indigo-200 bg-indigo-50/60 p-4 text-sm" data-testid="manual-unlock">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-bold">Customer paid but isn't Plus yet?</div>
          <div className="text-xs text-muted-foreground">Enter the email they signed up with on NxT (check Razorpay for the payer email), pick the plan, and unlock instantly.</div>
          {st && (
            <div className="mt-1 text-[11px]" data-testid="auto-sync-status">
              {st.api_mode ? (
                <span className="font-semibold text-emerald-700">● Auto-sync every {st.interval_min} min{last?.ran_at ? ` · last run ${new Date(last.ran_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} · ${last.activated?.length || 0} activated · ${last.already_synced || 0} in sync${last.unmatched?.length ? ` · ${last.unmatched.length} unmatched` : ""}` : " · first run pending"}</span>
              ) : (
                <span className="font-semibold text-rose-700">○ Auto-sync paused — add the Razorpay Key Secret (Razorpay credentials above) to enable it</span>
              )}
            </div>
          )}
        </div>
        <button onClick={reconcile} disabled={!!busy} data-testid="razorpay-sync-btn" className="rounded-full border border-indigo-300 bg-white px-4 py-2 text-xs font-bold text-indigo-700 hover:bg-indigo-100 disabled:opacity-60">{busy === "sync" ? "Syncing…" : "Sync with Razorpay"}</button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="customer@email.com" data-testid="manual-unlock-email" className="min-w-[220px] flex-1 rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-indigo-500" />
        <select value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="manual-unlock-plan" className="rounded-xl border border-input bg-card px-3 py-2 text-sm"><option value="monthly">Monthly ₹199</option><option value="yearly">Annual ₹1,999</option></select>
        <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Razorpay payment id (optional)" data-testid="manual-unlock-ref" className="min-w-[200px] rounded-xl border border-input bg-card px-3 py-2 font-mono text-xs outline-none focus:border-indigo-500" />
        <button onClick={activate} disabled={!!busy || !email.includes("@")} data-testid="manual-unlock-btn" className="rounded-full bg-emerald-600 px-5 py-2 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-60">{busy === "act" ? "Unlocking…" : "Unlock Plus"}</button>
      </div>
      {sync && (
        <div className="mt-3 space-y-1 text-xs" data-testid="razorpay-sync-result">
          <div className="font-semibold">Razorpay sync: {sync.activated.length} activated · {sync.already_synced} already in sync · {sync.unmatched.length} unmatched</div>
          {sync.activated.map((a) => <div key={a.subscription_id} className="text-emerald-700">✓ {a.email} → {a.plan} ({a.subscription_id})</div>)}
          {sync.unmatched.map((u) => <div key={u.subscription_id} className="text-amber-800">! {u.subscription_id} · payer {u.payer_email || "unknown"} · {u.reason === "no_user" ? "no NxT account with this email — use Unlock Plus above with their signup email" : u.reason}</div>)}
        </div>
      )}
    </div>
  );
}

function PendingActivations({ onChange }) {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/pending-activations").then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const approve = async (i) => {
    try { await api.post("/admin/activate", { user_id: i.user_id, plan: i.plan, payment_ref: `admin_${Date.now()}` }); toast.success(`Activated NxT Plus for ${i.email}`); load(); onChange?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reject = async (i) => {
    try { await api.post("/admin/reject-activation", { intent_id: i.id }); toast.success("Rejected"); load(); onChange?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!d) return null;
  const pending = d.intents.filter((i) => i.status === "pending_verification");
  const opened = d.intents.filter((i) => i.status === "opened");
  return (
    <Section title={`Pending activations (${pending.length})`}>
      <ManualUnlock onDone={() => { load(); onChange?.(); }} />
      {pending.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-pending-activations">No one is waiting for verification.</p>}
      <div className="space-y-2">
        {pending.map((i) => (
          <div key={i.id} data-testid={`pending-intent-${i.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm">
            <div>
              <div className="font-bold">{i.name || "—"} · <span className="font-mono text-xs">{i.email}</span></div>
              <div className="text-xs text-muted-foreground">{i.plan} · ₹{i.amount} · claimed {i.claimed_at ? new Date(i.claimed_at).toLocaleString("en-IN") : "—"}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => approve(i)} data-testid={`approve-${i.id}`} className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700">Approve</button>
              <button onClick={() => reject(i)} data-testid={`reject-${i.id}`} className="rounded-full border border-border px-4 py-2 text-xs font-bold text-muted-foreground hover:text-destructive">Reject</button>
            </div>
          </div>
        ))}
      </div>
      {opened.length > 0 && (
        <details className="mt-4 text-sm"><summary className="cursor-pointer font-semibold text-muted-foreground">Opened checkout, not yet claimed ({opened.length})</summary>
          <div className="mt-2 space-y-1">{opened.map((i) => <div key={i.id} className="flex justify-between rounded-xl bg-secondary px-3 py-2 text-xs"><span className="font-mono">{i.email}</span><span>{i.plan} · ₹{i.amount}</span></div>)}</div>
        </details>
      )}
      {d.unmatched.length > 0 && (
        <details className="mt-4 text-sm"><summary className="cursor-pointer font-semibold text-rose-600">Webhook payments not matched to a user ({d.unmatched.length})</summary>
          <div className="mt-2 space-y-1">{d.unmatched.map((u) => <div key={u.id} className="flex justify-between rounded-xl bg-rose-50 px-3 py-2 text-xs"><span className="font-mono">{u.email || "no email"}</span><span>₹{Math.round((u.amount || 0) / 100)} · {u.payment_id}</span></div>)}</div>
        </details>
      )}
    </Section>
  );
}

function ChangePassword() {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) { toast.error("New passwords don't match"); return; }
    setBusy(true);
    try { const { data } = await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password }); toast.success(data.message); setF({ current_password: "", new_password: "", confirm: "" }); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const field = (k, label, testId) => (
    <label className="text-xs font-semibold">{label}
      <input type="password" required value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={testId} autoComplete={k === "current_password" ? "current-password" : "new-password"}
        className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm font-normal outline-none focus:border-indigo-500" />
    </label>
  );
  return (
    <Section title="Security · Admin password">
      <p className="mb-3 text-xs text-muted-foreground">The default login (admin123) must not stay live. Set your own password — it survives restarts and deploys.</p>
      <form onSubmit={submit} className="grid gap-3 sm:grid-cols-3" data-testid="change-password-form">
        {field("current_password", "Current password", "current-password-input")}
        {field("new_password", "New password (8+, uppercase, number)", "new-password-input")}
        {field("confirm", "Confirm new password", "confirm-password-input")}
        <button type="submit" disabled={busy} data-testid="change-password-btn" className="rounded-full bg-slate-900 px-5 py-2.5 text-xs font-bold text-white hover:bg-indigo-600 disabled:opacity-60 sm:col-span-3 sm:w-fit">{busy ? "Updating…" : "Update password"}</button>
      </form>
    </Section>
  );
}

function TestConnection() {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/payments/test-connection"); setRes(data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-4">
      <button onClick={run} disabled={busy} data-testid="test-connection-btn" className="rounded-full border border-border px-4 py-2 text-xs font-bold hover:bg-secondary disabled:opacity-60">{busy ? "Testing…" : "Test connection"}</button>
      {res && (
        <ul className="mt-3 space-y-1 text-xs" data-testid="test-connection-result">
          {res.checks.map((c) => <li key={c.check} className="flex items-start gap-2"><span>{c.ok ? "🟢" : "🔴"}</span><span><b>{c.check}</b> — {c.detail}</span></li>)}
        </ul>
      )}
    </div>
  );
}

const CMS_KINDS = [["missions", "Missions"], ["quests", "Quests"], ["questions", "Assessment Qs"], ["achievements", "Achievements"], ["archetypes", "Archetypes"], ["prompts", "AI Prompts"]];
const HIDDEN = new Set(["overridden", "key", "id"]);

function CmsItemEditor({ kind, item, onDone }) {
  const idf = kind === "questions" ? "id" : "key";
  const [form, setForm] = useState(() => Object.fromEntries(Object.entries(item).filter(([k]) => !HIDDEN.has(k)).map(([k, v]) => [k, typeof v === "object" ? JSON.stringify(v, null, 1) : v])));
  const [busy, setBusy] = useState(false);
  const isObj = (k) => typeof item[k] === "object" && item[k] !== null;
  const save = async () => {
    setBusy(true);
    try {
      const data = {};
      for (const [k, v] of Object.entries(form)) {
        if (isObj(k)) data[k] = JSON.parse(v);
        else if (typeof item[k] === "number") data[k] = Number(v);
        else data[k] = v;
      }
      await api.put(`/admin/cms/content/${kind}`, { key: item[idf], data });
      toast.success("Saved — live immediately"); onDone();
    } catch (e) { toast.error(e?.message?.includes("JSON") ? "Invalid JSON in a field" : apiError(e)); } finally { setBusy(false); }
  };
  const reset = async () => {
    try { await api.delete(`/admin/cms/content/${kind}/${item[idf]}`); toast.success("Reset to default"); onDone(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-4" data-testid={`cms-editor-${item[idf]}`}>
      <div className="mb-2 font-mono text-xs text-muted-foreground">{idf}: {item[idf]}</div>
      <div className="grid gap-2 sm:grid-cols-2">
        {Object.entries(form).map(([k, v]) => (
          <label key={k} className={`text-xs font-semibold ${isObj(k) || String(v).length > 80 ? "sm:col-span-2" : ""}`}>{k}
            {isObj(k) || String(v).length > 80 ? (
              <textarea value={v} onChange={(e) => setForm({ ...form, [k]: e.target.value })} rows={isObj(k) ? 5 : 3} data-testid={`cms-field-${k}`} className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 font-mono text-xs outline-none focus:border-indigo-500" />
            ) : (
              <input value={v} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`cms-field-${k}`} className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 text-sm font-normal outline-none focus:border-indigo-500" />
            )}
          </label>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <button onClick={save} disabled={busy} data-testid="cms-save-btn" className="rounded-full bg-indigo-600 px-5 py-2 text-xs font-bold text-white hover:bg-indigo-700 disabled:opacity-60">Save</button>
        {item.overridden && <button onClick={reset} data-testid="cms-reset-btn" className="rounded-full border border-border px-4 py-2 text-xs font-bold text-muted-foreground hover:text-destructive">Reset to default</button>}
        <button onClick={onDone} className="rounded-full px-4 py-2 text-xs font-bold text-muted-foreground">Cancel</button>
      </div>
    </div>
  );
}

function ContentCMS() {
  const [kind, setKind] = useState("missions");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const idf = kind === "questions" ? "id" : "key";
  const load = () => api.get(`/admin/cms/content/${kind}`).then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  useEffect(() => { setEditing(null); load(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Section title="Content Manager">
      <div className="mb-4 flex flex-wrap gap-1.5" data-testid="cms-kinds">
        {CMS_KINDS.map(([k, l]) => <button key={k} onClick={() => setKind(k)} data-testid={`cms-kind-${k}`} className={`rounded-full px-3 py-1.5 text-xs font-bold ${kind === k ? "bg-slate-900 text-white" : "border border-border hover:bg-secondary"}`}>{l}</button>)}
      </div>
      <p className="mb-3 text-xs text-muted-foreground">Edits apply instantly across the app (no deploy). Overridden items are marked; reset restores the default.</p>
      <div className="space-y-2">
        {items.map((it) => editing === it[idf] ? (
          <CmsItemEditor key={it[idf]} kind={kind} item={it} onDone={() => { setEditing(null); load(); }} />
        ) : (
          <button key={it[idf]} onClick={() => setEditing(it[idf])} data-testid={`cms-item-${it[idf]}`}
            className="flex w-full items-center justify-between gap-3 rounded-2xl border border-border p-3 text-left text-sm hover:bg-secondary">
            <span className="min-w-0"><span className="block truncate font-bold">{it.title || it.name || it.text || it.key}</span><span className="font-mono text-[10px] text-muted-foreground">{it[idf]}</span></span>
            {it.overridden && <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">edited</span>}
          </button>
        ))}
      </div>
    </Section>
  );
}

function WebhookSetup({ d, webhookUrl }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(webhookUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { toast.error("Copy failed. Select the webhook URL instead."); } };
  const events = "subscription.authenticated, subscription.activated, subscription.charged, subscription.pending, subscription.halted, subscription.cancelled, subscription.completed, subscription.paused, subscription.resumed, subscription.updated, payment.captured, payment.failed, refund.processed";
  return <Section title="Razorpay Webhook · server confirmation">
    <div className="flex min-w-0 items-center gap-2"><code data-testid="webhook-url" className="min-w-0 flex-1 select-all overflow-x-auto rounded-xl bg-secondary p-3 text-xs">{webhookUrl}</code><button data-testid="copy-webhook-url" onClick={copy} className="rounded-full border px-3 py-2 text-xs font-bold">{copied ? "Copied" : "Copy"}</button></div>
    <ol className="mt-3 list-decimal space-y-2 pl-5 text-xs text-muted-foreground" data-testid="webhook-setup-instructions"><li>Razorpay Dashboard → LIVE mode → Account &amp; Settings → Webhooks. Add the URL above.</li><li>Use exactly the same secret as the backend environment variable RAZORPAY_WEBHOOK_SECRET. Secrets cannot be entered or stored in this dashboard.</li><li className="break-words">Enable events: {events}</li><li>A configured secret is not proof of delivery. Premium activates only after a verified capture and signed webhook; public launch still requires founder approval.</li></ol>
    <p data-testid="webhook-status" className="mt-3 text-xs font-bold">{d.webhook_connected ? "Webhook secret configured in the server environment" : "Webhook secret missing — checkout and activation remain locked"}</p>
    <WebhookDeliveries />
  </Section>;
}

function WebhookDeliveries() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/webhook-events").then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const color = { processed: "bg-emerald-100 text-emerald-700", failed: "bg-rose-100 text-rose-700", processing: "bg-amber-100 text-amber-700" };
  return (
    <details className="mt-4 text-sm" data-testid="webhook-deliveries">
      <summary className="cursor-pointer font-semibold">Recent webhook deliveries · {d.counts.processed} processed · {d.counts.failed} failed{d.unmatched ? ` · ${d.unmatched} unmatched` : ""}</summary>
      <div className="mt-2 space-y-1">
        {d.events.length === 0 && <p className="text-xs text-muted-foreground">No webhook events received yet.</p>}
        {d.events.map((e) => (
          <div key={e.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-secondary px-3 py-2 text-xs" data-testid={`webhook-event-${e.id}`}>
            <span className="font-mono">{e.type}</span>
            <span className="text-muted-foreground">{new Date(e.ts).toLocaleString("en-IN")}{e.result && e.result !== "processed" ? ` · ${e.result}` : ""}{e.error ? ` · ${e.error}` : ""}</span>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${color[e.status] || color.processing}`}>{e.status}</span>
          </div>
        ))}
      </div>
    </details>
  );
}

const STATUS_STYLE = { sent: "bg-emerald-100 text-emerald-700", failed: "bg-rose-100 text-rose-700", mocked: "bg-amber-100 text-amber-700" };

function TestEmail({ onSent }) {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const send = async () => {
    setBusy(true); setRes(null);
    try { const { data } = await api.post("/admin/emails/test", { to }); setRes(data); data.ok ? toast.success(`Test email sent to ${to}`) : toast.error(data.error || "Delivery failed"); onSent(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mb-3 rounded-2xl border border-border p-3 text-sm">
      <div className="font-bold">Send test email</div>
      <div className="mt-2 flex flex-wrap gap-2">
        <input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@example.com" data-testid="test-email-input"
          className="min-w-[220px] flex-1 rounded-xl border border-input bg-card px-4 py-2.5 text-sm outline-none focus:border-indigo-500" />
        <button onClick={send} disabled={busy || !to.includes("@")} data-testid="send-test-email-btn" className="rounded-full bg-indigo-600 px-5 py-2 text-xs font-bold text-white hover:bg-indigo-700 disabled:opacity-50">{busy ? "Sending…" : "Send test"}</button>
      </div>
      {res && <p data-testid="test-email-result" className={`mt-2 text-xs font-semibold ${res.ok ? "text-emerald-600" : "text-rose-600"}`}>{res.ok ? "Delivered to Resend — check the inbox (and spam)." : `Failed: ${res.error}`}</p>}
    </div>
  );
}

function RenewalReminders({ onRan }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/renewal-reminders/status").then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/run-renewal-reminders"); toast.success(`Renewal reminders: ${data.sent.length} sent · ${data.skipped_already_reminded} already reminded`); load(); onRan?.(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (!d) return null;
  return (
    <div className="mb-3 rounded-2xl border border-border p-3 text-sm" data-testid="renewal-reminders">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-bold">Renewal reminders</div>
          <div className="text-xs text-muted-foreground">Annual subscribers {d.lead_days.yearly} days before the charge, monthly {d.lead_days.monthly} days before · checks hourly{d.last?.ran_at ? ` · last run ${new Date(d.last.ran_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} (${d.last.sent?.length || 0} sent)` : ""}</div>
        </div>
        <button onClick={run} disabled={busy} data-testid="run-renewal-reminders-btn" className="rounded-full border border-border px-4 py-2 text-xs font-bold hover:bg-secondary disabled:opacity-60">{busy ? "Running…" : "Run now"}</button>
      </div>
      {d.upcoming?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="upcoming-renewals">
          {d.upcoming.slice(0, 8).map((u, i) => <span key={i} className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${u.reminded ? "bg-emerald-100 text-emerald-700" : "bg-secondary text-muted-foreground"}`}>{u.plan} · ₹{u.amount} · {u.charge_at}{u.reminded ? " ✓" : ""}</span>)}
        </div>
      )}
    </div>
  );
}

function EmailOutbox() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/emails").then(({ data }) => setD(data)).catch(() => setD({ emails: [], mocked: true }));
  useEffect(() => { load(); }, []);
  const runReminders = async () => {
    try { const { data } = await api.post("/admin/run-reminders"); toast.success(`Reminders sent: ${data.stage1} first nudges, ${data.stage2} final nudges`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!d) return null;
  const emails = d.emails;
  return (
    <Section title={`Email outbox · ${emails.length}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-border p-3 text-sm">
        <div><div className="font-bold">Payment reminders</div><div className="text-xs text-muted-foreground">Auto-nudge unpaid checkouts 1h and 24h after opening (runs every 10 min).</div></div>
        <button onClick={runReminders} data-testid="run-reminders-btn" className="rounded-full border border-border px-4 py-2 text-xs font-bold hover:bg-secondary">Run now</button>
      </div>
      <RenewalReminders onRan={load} />
      <WeeklyDigest onSent={load} />
      {d.mocked ? (
        <p className="mb-3 rounded-xl bg-amber-50 px-4 py-2 text-xs font-bold text-amber-800" data-testid="email-mocked-banner">MOCKED — no provider key configured. Emails are composed and stored here; add RESEND_API_KEY to deliver them.</p>
      ) : (
        <p className="mb-3 rounded-xl bg-emerald-50 px-4 py-2 text-xs font-bold text-emerald-800" data-testid="email-live-banner">Resend configured — sending as {d.sender_name} &lt;{d.sender}&gt;. Replies and customer feedback go to {d.reply_to}. Receipt delivery is confirmed by signed provider events; key configuration alone is not delivery proof.{d.sender.endsWith("@resend.dev") && <span className="block font-normal text-emerald-700">Sandbox sender: verify a domain and set SENDER_EMAIL before emailing customers.</span>}</p>
      )}
      {!d.mocked && <TestEmail onSent={load} />}
      {emails.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-emails">No emails yet.</p>}
      <div className="space-y-2">
        {emails.map((e) => (
          <div key={e.id} data-testid={`email-${e.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-border p-3 text-sm">
            <div className="min-w-0">
              <div className="font-bold">{e.subject}</div>
              <div className="text-xs text-muted-foreground"><span className="font-mono">{e.to}</span> · {e.kind} · {new Date(e.ts).toLocaleString("en-IN")}</div>
              {e.error && <div className="mt-1 truncate text-[11px] text-rose-600" title={e.error}>{e.error}</div>}
            </div>
            <div className="flex items-center gap-2">
              <span data-testid={`email-status-${e.id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_STYLE[e.status] || STATUS_STYLE.mocked}`}>{e.status}</span>
              <a href={`${API}/admin/emails/${e.id}/html`} target="_blank" rel="noreferrer" onClick={(ev) => { ev.preventDefault(); openEmail(e.id); }}
                data-testid={`preview-email-${e.id}`} className="text-xs font-bold text-indigo-600 hover:underline">Preview</a>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function RecentPayments() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/payments").then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const refund = async (p, cancel) => {
    if (!window.confirm(`Refund ₹${p.amount} to ${p.user_email}${cancel ? " and cancel their subscription (access ends now)" : ""}?`)) return;
    setBusy(p.payment_id);
    try { const { data } = await api.post(`/admin/payments/${p.payment_id}/refund`, { cancel_subscription: cancel, reason: "smoke_test" }); toast.success(`Refunded${data.real ? ` · ${data.refund_id}` : " (mock)"}${data.subscription_cancelled ? " · subscription cancelled" : ""}`); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  if (!d) return null;
  return (
    <Section title={`Verified customer receipts · ${d.payments.length}`}>
      {d.payments.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-payments">No verified live customer receipts. Test, manual and unverified records are available under Data quality; checkout remains disabled.</p>}
      <div className="space-y-2">
        {d.payments.map((p) => (
          <div key={p.id} data-testid={`admin-payment-${p.payment_id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-border p-3 text-sm">
            <div className="min-w-0">
              <div className="font-bold">₹{p.amount} · {p.plan}{p.event === "subscription.upgrade" ? " upgrade" : ""} · <span className="font-normal text-muted-foreground">{p.user_email || p.user_id}</span></div>
              <div className="text-xs text-muted-foreground"><span className="font-mono">{p.payment_id}</span>{p.subscription_id ? <> · <span className="font-mono">{p.subscription_id}</span></> : null} · {p.method || p.source} · {new Date(p.paid_at || p.ts).toLocaleString("en-IN")}</div>
            </div>
            <div className="flex items-center gap-2">
              <span data-testid={`admin-payment-status-${p.payment_id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${p.status === "refunded" ? "bg-slate-200 text-slate-700" : p.status === "captured" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>{p.status}</span>
              {user?.role === "super_admin" && p.status !== "refunded" && (
                <>
                  <button onClick={() => refund(p, false)} disabled={!!busy} data-testid={`refund-btn-${p.payment_id}`} className="rounded-full border border-border px-3 py-1.5 text-xs font-bold hover:bg-secondary disabled:opacity-60">{busy === p.payment_id ? "…" : "Refund"}</button>
                  <button onClick={() => refund(p, true)} disabled={!!busy} data-testid={`refund-cancel-btn-${p.payment_id}`} className="rounded-full bg-rose-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-rose-700 disabled:opacity-60">Refund + cancel</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function DemoAccount() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const load = () => api.get("/admin/demo").then(({ data }) => setD(data)).catch(() => setD(null));
  useEffect(() => { load(); }, []);
  const reset = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/demo/reset"); toast.success(`Demo reset — ${data.restored} records restored`); setD(data.status); setConfirm(false); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const snapshot = async () => {
    try { const { data } = await api.post("/admin/demo/snapshot"); toast.success(`Snapshot saved (${data.docs} records) — this is now the reset point`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!d) return null;
  const s = d.stats;
  const stat = (label, v, id) => <div className="rounded-xl bg-secondary px-3 py-2"><div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</div><div className="font-display text-base font-bold" data-testid={id}>{v}</div></div>;
  return (
    <Section title="Plus demo account · replay the paid journey">
      <div className="flex flex-wrap items-start justify-between gap-3 text-sm">
        <div>
          <div className="font-mono text-xs" data-testid="demo-email">{d.email} <span className="text-muted-foreground">/ {d.password}</span></div>
          <div className="mt-1 text-xs text-muted-foreground">
            {d.exists ? `Yearly Plus · target ${s.target_career || "—"}` : "Account missing — reset will recreate it"}
            {d.snapshot?.last_reset_at && <> · last reset {new Date(d.snapshot.last_reset_at).toLocaleString("en-IN")}</>}
          </div>
        </div>
        <div className="flex gap-2">
          {!confirm ? (
            <button onClick={() => setConfirm(true)} data-testid="demo-reset-btn" className="rounded-full bg-slate-900 px-5 py-2 text-xs font-bold text-white hover:bg-indigo-600">Reset demo</button>
          ) : (
            <>
              <button onClick={reset} disabled={busy} data-testid="demo-reset-confirm-btn" className="rounded-full bg-rose-600 px-5 py-2 text-xs font-bold text-white hover:bg-rose-700 disabled:opacity-60">{busy ? "Resetting…" : "Yes, wipe & restore"}</button>
              <button onClick={() => setConfirm(false)} data-testid="demo-reset-cancel-btn" className="rounded-full border border-border px-4 py-2 text-xs font-bold text-muted-foreground">Cancel</button>
            </>
          )}
        </div>
      </div>
      {s && (
        <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-6" data-testid="demo-stats">
          {stat("XP", s.xp, "demo-xp")}{stat("Streak", `${s.streak}d`, "demo-streak")}{stat("Readiness", `${s.readiness}%`, "demo-readiness")}
          {stat("Quests", `${s.quests_completed}/${s.quests}`, "demo-quests")}{stat("Proofs", s.proofs, "demo-proofs")}{stat("Coach msgs", s.coach_messages, "demo-coach")}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <span>Reset wipes everything the demo user did (missions, chats, proofs, XP) and restores the showcase state, dated as if active yesterday. Only super admins see this.</span>
        <button onClick={snapshot} data-testid="demo-snapshot-btn" className="rounded-full border border-border px-3 py-1.5 text-[11px] font-bold hover:bg-secondary">Save current state as reset point</button>
      </div>
    </Section>
  );
}

async function openEmail(id) {
  try {
    const { data } = await api.get(`/admin/emails/${id}/html`, { responseType: "text" });
    const w = window.open("", "_blank");
    if (w) { w.document.write(data); w.document.close(); }
  } catch (e) { toast.error(apiError(e)); }
}

function RazorpayConfig() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/razorpay-config").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return null;
  return <Section title="Razorpay configuration · environment only">
    <p className="mb-4 text-sm text-muted-foreground" data-testid="rzp-env-only-notice">Set credentials in the backend deployment environment, never in this dashboard. Restart or redeploy after changes. No secret value is returned to the browser or saved in MongoDB.</p>
    <div className="space-y-2" data-testid="rzp-cfg-status">
      <Row label="RAZORPAY_KEY_ID" value={<code data-testid="rzp-env-key-id">{d.values.key_id || "Missing"}</code>} />
      <Row label="RAZORPAY_MONTHLY_PLAN_ID" value={<code data-testid="rzp-env-plan-id">{d.values.monthly_plan || "Missing"}</code>} />
      <Row label="RAZORPAY_KEY_SECRET" value={<span data-testid="rzp-env-key-secret">{d.secrets_configured.key_secret ? "Configured on server" : "Missing"}</span>} />
      <Row label="RAZORPAY_WEBHOOK_SECRET" value={<span data-testid="rzp-env-webhook-secret">{d.secrets_configured.webhook_secret ? "Configured on server" : "Missing"}</span>} />
    </div>
  </Section>;
}

function PaymentSettings() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [monthly, setMonthly] = useState("");
  const [yearly, setYearly] = useState("");
  const load = () => api.get("/admin/payment-settings").then(({ data }) => { setD(data); setMonthly(data.price_monthly); setYearly(data.price_yearly); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.put("/admin/pricing", { price_monthly: Number(monthly), price_yearly: Number(yearly) }); toast.success("Pricing updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!d) return <Loader2 className="mx-auto my-20 h-8 w-8 animate-spin text-indigo-600" />;
  const webhookUrl = `${API}${d.webhook_path.replace(/^\/api/, "")}`;
  return (
    <div className="space-y-5">
      <PaymentsSwitch />
      <GoLiveChecklist />
      {user?.role === "super_admin" && <RazorpayConfig onSaved={load} />}
      <ChangePassword />
      <RecentPayments />
      {user?.role === "super_admin" && <details className="rounded-2xl border border-border p-4"><summary data-testid="admin-demo-tools-toggle" className="cursor-pointer text-sm font-bold">Internal demo tools — excluded from customer reporting</summary><div className="mt-4"><DemoAccount /></div></details>}
      <PendingActivations onChange={load} />
      <Section title="Payment · Razorpay">
        <div className="space-y-3 text-sm">
          <Row label="Provider" value={d.provider} />
          <Row label="Credential environment" value={<span data-testid="payment-env" className={`rounded-full px-2 py-0.5 text-xs font-bold ${d.environment === "LIVE" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{d.environment}</span>} />
          <Row label="Checkout type" value={d.checkout_type === "payment_link" ? "Hosted payment links" : d.checkout_type === "api" ? "Razorpay Subscriptions API (Checkout.js)" : d.checkout_type} />
          <Row label="Status" value={d.connected ? <span className="font-bold text-emerald-600">🟢 Connected</span> : <span className="font-bold text-rose-600">🔴 Not Connected</span>} />
          <Row label="Webhook" value={d.webhook_connected ? "🟢 Secret configured" : "🟠 Secret missing — auto-unlock off"} />
          {d.checkout_type === "api" ? (
            <>
              <Row label="Key ID" value={<code className="font-mono text-xs" data-testid="rzp-key-id">{d.key_id}</code>} />
              <Row label="Monthly plan" value={<code className="font-mono text-xs" data-testid="rzp-monthly-plan">{d.monthly_plan_id}</code>} />
              <Row label="Annual plan" value={<code className="font-mono text-xs" data-testid="rzp-yearly-plan">{d.yearly_plan_id}</code>} />
            </>
          ) : (
            <>
              <Row label="Monthly link" value={d.monthly_link ? <a href={d.monthly_link} target="_blank" rel="noreferrer" className="text-indigo-600 underline">{d.monthly_link}</a> : "—"} />
              <Row label="Annual link" value={d.yearly_link ? <a href={d.yearly_link} target="_blank" rel="noreferrer" className="text-indigo-600 underline">{d.yearly_link}</a> : "—"} />
            </>
          )}
        </div>
        {d.mock_mode && <p className="mt-4 rounded-xl bg-amber-50 px-4 py-2.5 text-xs font-bold text-amber-800">Legacy configuration is in test mode. Public checkout remains disabled.</p>}
        <TestConnection />
      </Section>
      <WebhookSetup d={d} webhookUrl={webhookUrl} onSaved={load} />
      <EmailOutbox />
      <Section title="Pricing (editable)">
        <div className="grid grid-cols-2 gap-4">
          <div><label className="text-sm font-semibold">Monthly (₹)</label><input type="number" value={monthly} onChange={(e) => setMonthly(e.target.value)} data-testid="admin-price-monthly" className="mt-1.5 w-full rounded-xl border border-input bg-card px-4 py-3 outline-none focus:border-indigo-500" /></div>
          <div><label className="text-sm font-semibold">Annual (₹)</label><input type="number" value={yearly} onChange={(e) => setYearly(e.target.value)} data-testid="admin-price-yearly" className="mt-1.5 w-full rounded-xl border border-input bg-card px-4 py-3 outline-none focus:border-indigo-500" /></div>
        </div>
        <button onClick={save} data-testid="admin-save-pricing" className="mt-4 rounded-full bg-indigo-600 px-6 py-3 font-bold text-white hover:bg-indigo-700">Save pricing</button>
      </Section>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex items-center justify-between border-b border-border/50 py-2 last:border-0"><span className="text-muted-foreground">{label}</span><span className="font-semibold">{value}</span></div>;
}

function CMS() {
  const [careers, setCareers] = useState([]);
  useEffect(() => { api.get("/admin/cms/careers").then(({ data }) => setCareers(data)).catch(() => {}); }, []);
  const save = async (c) => {
    try { await api.put("/admin/cms/careers", c); toast.success("Saved"); } catch (e) { toast.error(apiError(e)); }
  };
  const exportCsv = async (report) => {
    const t = localStorage.getItem("nxt_token");
    const res = await fetch(`${API}/admin/export/${report}`, { headers: { Authorization: `Bearer ${t}` } });
    const blob = await res.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `nxt_${report}.csv`; a.click();
  };
  return (
    <div className="space-y-5">
      <Section title="Data Exports">
        <div className="flex flex-wrap gap-3">
          {["users", "revenue", "careers"].map((r) => (
            <button key={r} onClick={() => exportCsv(r)} data-testid={`export-${r}`} className="flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-bold hover:bg-secondary"><Download className="h-4 w-4" /> {r} CSV</button>
          ))}
        </div>
      </Section>
      <TrustCms />
      <ContentCMS />
      <Section title="Career Content (CMS)">
        <div className="space-y-3">
          {careers.map((c) => (
            <details key={c.key} className="rounded-2xl border border-border p-3" data-testid={`cms-career-${c.key}`}>
              <summary className="cursor-pointer font-semibold">{c.name}</summary>
              <div className="mt-3 space-y-2">
                <textarea defaultValue={c.description} rows={2} onChange={(e) => (c.description = e.target.value)} className="w-full rounded-xl border border-input bg-card px-3 py-2 text-sm" />
                <div className="grid grid-cols-2 gap-2">
                  <input defaultValue={c.salary_entry} onChange={(e) => (c.salary_entry = e.target.value)} className="rounded-xl border border-input bg-card px-3 py-2 text-sm" placeholder="Entry salary" />
                  <input defaultValue={c.salary_mid} onChange={(e) => (c.salary_mid = e.target.value)} className="rounded-xl border border-input bg-card px-3 py-2 text-sm" placeholder="Mid salary" />
                </div>
                <button onClick={() => save(c)} className="rounded-full bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-600">Save</button>
              </div>
            </details>
          ))}
        </div>
      </Section>
    </div>
  );
}

const TABS = [
  { key: "overview", label: "Founder brief", icon: LayoutDashboard, comp: FounderDashboard },
  { key: "users", label: "Customers", icon: Users, comp: FounderCustomers },
  { key: "quality", label: "Data quality & test view", icon: Filter, comp: FounderDataQuality },
  { key: "cms", label: "Content & testimonials", icon: FileEdit, comp: CMS, operations: true },
  { key: "accounts", label: "Account support & grants", icon: Users, comp: UsersTab, operations: true },
  { key: "payments", label: "Billing & operations", icon: Settings, comp: PaymentSettings, operations: true },
];

function BuildBanner() {
  const [v, setV] = useState(null);
  useEffect(() => { api.get("/version").then(({ data }) => setV(data)).catch(() => setV({ error: true })); }, []);
  if (!v) return null;
  const issues = [];
  if (v.error) issues.push("Backend unreachable — cannot verify the deployed build.");
  else {
    if (v.version !== APP_VERSION) issues.push(`Frontend is v${APP_VERSION} but backend is v${v.version || "unknown"} — this deployment is out of sync. Redeploy the latest build.`);
    if (v.payment?.environment === "LIVE" && v.payment?.checkout_type !== "api") issues.push(`Live payments are using ${v.payment.checkout_type === "payment_link" ? "hosted payment links" : "mock checkout"} instead of the Razorpay Subscriptions API — the deployed .env is missing RAZORPAY_KEY_ID / plan IDs (stale deployment). Redeploy.`);
    if (v.payment?.checkout_type === "link" && v.payment?.monthly_link && /ieR4KmI|jQQbwGt|Qo1MUced|3VjJkmfF/.test(`${v.payment.monthly_link} ${v.payment.yearly_link || ""}`)) issues.push("A payment link points to a cancelled Razorpay page. Redeploy with the current build.");
    if (v.payment?.environment === "LIVE" && v.payment?.checkout_type === "payment_link") issues.push("Hosted subscription links are single-use — the first customer who pays locks the link for everyone else. Add the Razorpay Key Secret (Payment Settings → Razorpay credentials, or Publish → Secrets) to switch to per-customer Checkout.");
  }
  const stale = issues.length > 0;
  return (
    <div data-testid="build-banner" className={`mb-4 flex flex-wrap items-center justify-between gap-2 rounded-2xl border px-4 py-2.5 text-xs ${stale ? "border-rose-300 bg-rose-50 text-rose-900" : "border-border bg-card text-muted-foreground"}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className={`font-bold ${stale ? "text-rose-700" : "text-emerald-600"}`} data-testid="build-status">{stale ? "⚠ Stale deployment" : "● Build up to date"}</span>
        <span data-testid="build-version">v{v.version || "?"}{v.commit ? ` · ${v.commit}` : ""}{v.commit_date ? ` · ${new Date(v.commit_date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}` : ""}</span>
        {v.payment && <span>· {v.payment.environment} · {v.payment.checkout_type === "api" ? `Razorpay API · ${v.payment.monthly_plan_id}` : v.payment.checkout_type}</span>}
        {v.started_at && <span className="hidden sm:inline">· backend started {new Date(v.started_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
      </div>
      {stale && <ul className="w-full list-disc pl-5 font-semibold" data-testid="build-issues">{issues.map((i, k) => <li key={k}>{i}</li>)}</ul>}
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [customerFilter, setCustomerFilter] = useState({ initialStatus: "all" });
  const [customerVisit, setCustomerVisit] = useState(0);
  const selected = TABS.find((t) => t.key === tab);
  const Active = selected.comp;
  const activeProps = tab === "overview" ? { onQuality: () => setTab("quality"), onCustomers: (status, period) => { setCustomerFilter({ initialStatus: status, initialPeriod: period }); setTab("users"); } } : tab === "users" ? customerFilter : {};

  return (
    <div className="flex min-h-screen bg-[#0b1220]">
      {/* sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 h-screen w-64 shrink-0 transform border-r border-slate-700 bg-slate-900 text-slate-200 transition-transform md:sticky md:top-0 md:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center justify-between p-5"><div className="rounded-xl bg-white px-3 py-2"><Logo /></div><button data-testid="admin-close-menu" aria-label="Close admin menu" className="md:hidden" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button></div>
        <div className="px-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Admin Dashboard</div>
        <nav className="mt-2 space-y-1 px-3">
          {TABS.map((t) => (
            <div key={t.key}>{t.key === "cms" && <p className="mb-2 mt-7 px-3 text-[10px] font-bold uppercase tracking-widest text-slate-500">Operations · separate from metrics</p>}<button onClick={() => { setTab(t.key); if (t.key === "users") { setCustomerFilter({ initialStatus: "all" }); setCustomerVisit(visit => visit + 1); } setOpen(false); }} data-testid={`admin-tab-${t.key}`}
              className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold transition-colors ${tab === t.key ? "bg-emerald-300 text-slate-950" : "text-slate-400 hover:bg-slate-800 hover:text-white"}`}>
              <t.icon className="h-4 w-4 shrink-0" /> {t.label}
            </button></div>
          ))}
        </nav>
        <div className="absolute bottom-0 w-full border-t border-border p-3">
          <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout" className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-muted-foreground hover:bg-secondary"><LogOut className="h-4 w-4" /> Log out</button>
        </div>
      </aside>
      {open && <div data-testid="admin-menu-overlay" className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={() => setOpen(false)} />}

      <main className="min-w-0 flex-1 overflow-x-hidden">
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-slate-800 bg-[#0b1220]/95 px-5 py-4 text-slate-200 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <button data-testid="admin-open-menu" aria-label="Open admin menu" className="md:hidden" onClick={() => setOpen(true)}><Menu className="h-5 w-5" /></button>
            <h1 className="font-display text-xl font-extrabold">{TABS.find((t) => t.key === tab).label}</h1>
          </div>
          <div className="hidden text-sm text-slate-400 sm:block">{user?.email}</div>
        </header>
        <div className="mx-auto max-w-[1400px] p-4 sm:p-7"><div className={selected.operations ? "rounded-2xl bg-background p-4 text-foreground sm:p-6" : ""}>{tab === "payments" && <BuildBanner />}<Active key={tab === "users" ? `${tab}-${customerVisit}` : tab} {...activeProps} /></div></div>
      </main>
    </div>
  );
}
